/**
 * Contexto de Notificaciones
 * Sistema centralizado para mostrar toasts/notificaciones en toda la aplicación
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface NotificationContextType {
  notifications: Notification[];
  showNotification: (notification: Omit<Notification, 'id'>) => void;
  hideNotification: (id: string) => void;
  clearAll: () => void;
  // Métodos de conveniencia
  showSuccess: (title: string, message?: string, duration?: number) => void;
  showError: (title: string, message?: string, duration?: number) => void;
  showWarning: (title: string, message?: string, duration?: number) => void;
  showInfo: (title: string, message?: string, duration?: number) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const generateId = useCallback(() => {
    return `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const hideNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const showNotification = useCallback((notification: Omit<Notification, 'id'>) => {
    const id = generateId();
    const newNotification: Notification = {
      ...notification,
      id,
      duration: notification.duration ?? getDefaultDuration(notification.type),
    };

    setNotifications(prev => [...prev, newNotification]);

    // Auto-hide después del tiempo especificado
    if (newNotification.duration && newNotification.duration > 0) {
      setTimeout(() => {
        hideNotification(id);
      }, newNotification.duration);
    }
  }, [generateId, hideNotification]);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  // Métodos de conveniencia con duraciones optimizadas para UX
  const showSuccess = useCallback((title: string, message?: string, duration?: number) => {
    showNotification({
      type: 'success',
      title,
      message,
      duration: duration ?? 4000, // 4 segundos para éxito - tiempo suficiente para leer
    });
  }, [showNotification]);

  const showError = useCallback((title: string, message?: string, duration?: number) => {
    showNotification({
      type: 'error',
      title,
      message,
      duration: duration ?? 6000, // 6 segundos para errores - más tiempo para procesar
    });
  }, [showNotification]);

  const showWarning = useCallback((title: string, message?: string, duration?: number) => {
    showNotification({
      type: 'warning',
      title,
      message,
      duration: duration ?? 5000, // 5 segundos para advertencias
    });
  }, [showNotification]);

  const showInfo = useCallback((title: string, message?: string, duration?: number) => {
    showNotification({
      type: 'info',
      title,
      message,
      duration: duration ?? 4000, // 4 segundos para información
    });
  }, [showNotification]);

  const value: NotificationContextType = {
    notifications,
    showNotification,
    hideNotification,
    clearAll,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// Hook personalizado para usar las notificaciones
export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications debe ser usado dentro de un NotificationProvider');
  }
  return context;
};

// Función helper para duraciones por defecto basadas en investigación UX
function getDefaultDuration(type: NotificationType): number {
  switch (type) {
    case 'success':
      return 4000; // Éxito: 4s - suficiente para sentir satisfacción
    case 'error':
      return 6000; // Error: 6s - más tiempo para leer y procesar
    case 'warning':
      return 5000; // Advertencia: 5s - tiempo intermedio
    case 'info':
      return 4000; // Info: 4s - información rápida
    default:
      return 4000;
  }
}